import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import { ArrowLeft, ShieldAlert, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 6) return setError("Password must be at least 6 characters.");
    if (pw1 !== pw2) return setError("Passwords do not match.");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Reset failed. Request a new link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <BrandLogo size={40} />
          <p className="font-heading text-lg font-bold">NestVillage Care Metrics</p>
        </div>
        {!token ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-rose-900" data-testid="reset-no-token">
            <ShieldAlert className="text-rose-600 mb-2" />
            <h2 className="text-h3 font-bold">Invalid reset link</h2>
            <p className="text-sm mt-2">The link is missing a token. Please request a new password reset.</p>
            <Link to="/forgot-password" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-rose-800 hover:underline">
              <ArrowLeft size={14} /> Request new link
            </Link>
          </div>
        ) : done ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900" data-testid="reset-success">
            <CheckCircle2 className="text-emerald-600 mb-2" />
            <h2 className="text-h3 font-bold">Password updated</h2>
            <p className="text-sm mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h2 className="text-h1">Choose a new password</h2>
            <p className="text-sm text-slate-500 mt-1.5">Enter and confirm your new password.</p>
            <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="reset-form">
              <div>
                <label className="text-small-caps">New password</label>
                <input type="password" required minLength={6} value={pw1} onChange={(e) => setPw1(e.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
                  data-testid="reset-pw1" />
              </div>
              <div>
                <label className="text-small-caps">Confirm new password</label>
                <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400"
                  data-testid="reset-pw2" />
              </div>
              {error && <p className="text-xs text-rose-600 font-medium" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full px-4 py-2.5 rounded-lg bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-60"
                data-testid="reset-submit-btn">
                {submitting ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
